import { useQueries, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";
import TaskControlsService from "src/modules/data-protection/apis/TaskControlsService";
import { formatControlNumber } from "src/modules/data-protection/helpers/Controls";

const useIsmsControlOptions = ({ softwareId, language, t, enabled = true } = {}) => {
    const controlsOptions = { software_id: softwareId };
    const frameworksQuery = useQuery({
        queryKey: ["gdpr-fetch-isms-accessible-controls", controlsOptions],
        queryFn: async () => {
            const service = ComplianceFrameworkSettingsService.getInstance();

            return await service.fetchAccessibleControls({
                ...controlsOptions,
            });
        },
        enabled: Boolean(softwareId && enabled),
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toast(t("An error occurred while retrieving the controls list."), {
                type: "error",
            });
        },
    });
    const frameworks = frameworksQuery.data?.accessible_frameworks || [];
    const controlsQueries = useQueries({
        queries: frameworks.map((framework) => ({
            queryKey: ["gdpr-controls", language, framework],
            queryFn: async () => {
                const service = TaskControlsService.getInstance();
                const response = await service.fetchControls({
                    type: framework,
                });

                return (response || []).map((control) => ({
                    value: control.id,
                    label: `${formatControlNumber(control.control_number, framework)}. ${t(control.title)}`,
                    control,
                }));
            },
            enabled: Boolean(enabled && frameworksQuery.isSuccess),
            refetchOnWindowFocus: false,
            onError: (error) => {
                if (process.env.NODE_ENV === "development") {
                    console.error(error);
                }

                toast(t("An error occurred while retrieving the controls list."), {
                    type: "error",
                });
            },
        })),
    });
    const loadedFrameworks = frameworks.filter(
        (_framework, index) => controlsQueries[index]?.isSuccess
    );

    return {
        frameworks,
        loadedFrameworks,
        isLoading: Boolean(enabled) && (
            frameworksQuery.isLoading
            || controlsQueries.some((query) => query.isLoading)
        ),
        optionsByFramework: frameworks.reduce((map, framework, index) => {
            map[framework] = controlsQueries[index]?.data || [];

            return map;
        }, {}),
    };
};

export default useIsmsControlOptions;
